import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, getApiError } from '../../lib/api';
import { Button } from '../../components/Button';
import { IconButton } from '../../components/IconButton';
import { cn } from '../../lib/cn';
import { api } from '../../lib/api';

interface PermissionDef {
  key: string;
  label: string;
  description: string;
  group: string;
}

interface Overlay {
  permissionKey: string;
  mode: 'grant' | 'deny';
}

interface PermsResponse {
  user: { id: string; name: string; email: string; role: string };
  catalogue: PermissionDef[];
  roleDefaults: string[];
  overlays: Overlay[];
  effective: string[];
}

type State = 'inherit' | 'grant' | 'deny';

export function UserPermissionsModal({
  userId,
  onClose,
}: {
  userId: string;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const data = useQuery({
    queryKey: ['user-perms', userId],
    queryFn: () => apiGet<PermsResponse>(`/users/${userId}/permissions`),
  });

  const [state, setState] = useState<Record<string, State>>({});

  useEffect(() => {
    if (!data.data) return;
    const initial: Record<string, State> = {};
    for (const p of data.data.catalogue) initial[p.key] = 'inherit';
    for (const o of data.data.overlays) initial[o.permissionKey] = o.mode;
    setState(initial);
  }, [data.data]);

  const grouped = useMemo(() => {
    const out = new Map<string, PermissionDef[]>();
    for (const p of data.data?.catalogue ?? []) {
      const arr = out.get(p.group) ?? [];
      arr.push(p);
      out.set(p.group, arr);
    }
    return out;
  }, [data.data?.catalogue]);

  const dirty = useMemo(() => {
    if (!data.data) return false;
    const current = new Set(data.data.overlays.map((o) => `${o.permissionKey}:${o.mode}`));
    const next = new Set<string>();
    for (const [k, v] of Object.entries(state)) {
      if (v !== 'inherit') next.add(`${k}:${v}`);
    }
    if (current.size !== next.size) return true;
    for (const x of current) if (!next.has(x)) return true;
    return false;
  }, [data.data, state]);

  const save = useMutation({
    mutationFn: async () => {
      const overlays: Overlay[] = Object.entries(state)
        .filter(([, v]) => v !== 'inherit')
        .map(([permissionKey, mode]) => ({ permissionKey, mode: mode as 'grant' | 'deny' }));
      // PUT not PATCH — replaces the whole overlay set
      await api.put(`/users/${userId}/permissions`, { overlays });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-perms', userId] });
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
      <div className="bg-surface rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-rule flex items-start justify-between">
          <div>
            <h3 className="text-base font-semibold text-ink">Permissions</h3>
            {data.data && (
              <p className="text-xs text-ink-3 mt-0.5">
                {data.data.user.name} · role <code className="kc-mono">{data.data.user.role}</code>{' '}
                · changes apply on next login
              </p>
            )}
          </div>
          <IconButton onClick={onClose} aria-label="Close">
            ×
          </IconButton>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {data.isLoading || !data.data ? (
            <div className="text-sm text-ink-3">Loading…</div>
          ) : (
            <>
              <p className="text-xs text-ink-3">
                <span className="inline-block w-2 h-2 rounded-full bg-positive mr-1" />
                granted by role
                <span className="inline-block w-2 h-2 rounded-full bg-ink-4 ml-4 mr-1" />
                not granted by role
              </p>
              {[...grouped.entries()].map(([group, perms]) => (
                <section key={group}>
                  <h4 className="text-xs uppercase tracking-wider text-ink-3 font-semibold mb-2">
                    {group}
                  </h4>
                  <div className="border border-rule rounded divide-y divide-rule">
                    {perms.map((p) => {
                      const inRole = data.data!.roleDefaults.includes(p.key);
                      const cur = state[p.key] ?? 'inherit';
                      return (
                        <div key={p.key} className="flex items-center px-4 py-3 gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span
                                className={cn(
                                  'inline-block w-2 h-2 rounded-full',
                                  inRole ? 'bg-positive' : 'bg-ink-4',
                                )}
                              />
                              <span className="text-sm text-ink">{p.label}</span>
                            </div>
                            <div className="text-xs text-ink-3 mt-0.5">{p.description}</div>
                            <div className="text-[10px] text-ink-4 kc-mono mt-1">{p.key}</div>
                          </div>
                          <div className="flex bg-paper-2 border border-rule rounded p-0.5">
                            <Tri
                              active={cur === 'inherit'}
                              onClick={() => setState((s) => ({ ...s, [p.key]: 'inherit' }))}
                            >
                              Inherit
                            </Tri>
                            <Tri
                              active={cur === 'grant'}
                              onClick={() => setState((s) => ({ ...s, [p.key]: 'grant' }))}
                              tone="positive"
                            >
                              Grant
                            </Tri>
                            <Tri
                              active={cur === 'deny'}
                              onClick={() => setState((s) => ({ ...s, [p.key]: 'deny' }))}
                              tone="negative"
                            >
                              Deny
                            </Tri>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
              {save.isError && (
                <div className="text-sm text-negative bg-negative-soft px-3 py-2 rounded">
                  {getApiError(save.error).message}
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-rule flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!dirty}>
            Save overrides
          </Button>
        </div>
      </div>
    </div>
  );
}

function Tri({
  children,
  active,
  onClick,
  tone,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
  tone?: 'positive' | 'negative';
}): JSX.Element {
  const activeClass =
    tone === 'positive'
      ? 'bg-positive text-white'
      : tone === 'negative'
        ? 'bg-negative text-white'
        : 'bg-surface text-ink shadow-sm';
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3 py-1 text-xs rounded transition',
        active ? activeClass : 'text-ink-3 hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}
